import { useState } from 'react'
import { Check, ChevronLeft, ChevronRight, Trash2, X } from 'lucide-react'
import { formatDateTime } from '../patientFormat'
import NutritionRecordForm from './NutritionRecordForm'
import { OFFER_STATUS, consistencyLabel, dayHeading, formatPlateItem, incidentLabel, routeLabel, shiftDate } from './nutritionFormat'

function NutritionTimeline({ date, items, loading, failed, canEdit, canRemoveWater = false, canPlace, onDateChange, onRecord, onDeleteWater, onPlace }) {
  const [modeFor, setModeFor] = useState(null)
  const [mode, setMode] = useState('')
  const [over, setOver] = useState(false)

  const openMode = (item, next) => {
    setModeFor(item.id)
    setMode(next)
  }

  const closeMode = () => {
    setModeFor(null)
    setMode('')
  }

  return (
    <section
      className={`med-daily${over ? ' is-drop' : ''}`}
      data-onboarding="nutrition-timeline"
      onDragOver={(event) => {
        if (!canPlace) return
        event.preventDefault()
        setOver(true)
      }}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return
        setOver(false)
      }}
      onDrop={(event) => {
        if (!canPlace) return
        event.preventDefault()
        setOver(false)
        const plateId = event.dataTransfer.getData('text/plain')
        if (plateId) onPlace(plateId)
      }}
    >
      <div className="med-daily-head">
        <h3>Linha do tempo de refeições</h3>
        {canPlace && <p className="patients-muted med-daily-empty">Solte um prato aqui para escolher horário e período.</p>}
        <div className="med-day-nav">
          <button type="button" aria-label="Dia anterior" onClick={() => onDateChange(shiftDate(date, -1))}>
            <ChevronLeft size={16} />
          </button>
          <strong>{dayHeading(date)}</strong>
          <button type="button" aria-label="Próximo dia" onClick={() => onDateChange(shiftDate(date, 1))}>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      {loading && <p className="patients-muted med-daily-empty">Carregando o dia…</p>}
      {!loading && !failed && !items.length && <p className="patients-muted med-daily-empty">Nenhuma oferta ou hidratação neste dia.</p>}
      <div className={`med-rail${items.length ? '' : ' is-empty'}`}>
        {items.map((item) => {
          const status = item.effective_status || item.status
          const water = item.type === 'hidratacao'
          const actionable = canEdit && !water && (status === 'pendente' || status === 'atrasado')
          const open = modeFor === item.id
          const when = item.administered_at ? formatDateTime(item.administered_at) : null
          return (
            <article key={item.id} className="med-rail-item">
              <time>{item.scheduled_time || '—'}</time>
              <span className={`med-rail-dot is-${status}`} />
              <div className={`med-rail-card is-${status}`}>
                <strong>{water ? 'Hidratação' : (OFFER_STATUS[status] || status)}</strong>
                <p className="med-rail-drug">{item.name}</p>
                <p>
                  {water
                    ? `${item.volume_ml} ml`
                    : `${consistencyLabel(item.consistency) || routeLabel(item.route)}${item.volume_ml ? ` · ${item.volume_ml} ml` : ''}`}
                </p>
                {!water && item.notes && <p>{item.notes}</p>}
                {!water && item.items?.length > 0 && (
                  <div className="nut-plate-block">
                    <span>Servir</span>
                    <ul className="nut-plate">
                      {item.items.map((food, index) => <li key={`${index}-${food.food_name}`}>{formatPlateItem(food)}</li>)}
                    </ul>
                  </div>
                )}
                {!water && item.acceptance_percent != null && (
                  <p>Aceite {item.acceptance_percent}%{item.consumed_ml != null ? ` · ${item.consumed_ml} ml` : ''}</p>
                )}
                {item.incident && <p>Intercorrência: {incidentLabel(item.incident)}{item.incident_note ? ` · ${item.incident_note}` : ''}</p>}
                {item.reason && <p>{item.reason}</p>}
                <p>{item.administered_by_name || (water ? 'Água livre' : 'Aguardando aceite')}</p>
                {when && <p>{when}</p>}
                {actionable && (
                  <div className="med-dose-actions">
                    <button type="button" className="patients-btn patients-btn--green patients-btn--compact" onClick={() => onRecord(item, { outcome: 'total' })}>
                      <Check size={14} /> 100%
                    </button>
                    <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact" onClick={() => openMode(item, 'parcial')}>
                      Parcial
                    </button>
                    <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact" onClick={() => openMode(item, 'recusa')}>
                      <X size={14} /> Recusar
                    </button>
                    <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact" onClick={() => openMode(item, 'incidente')}>
                      Intercorrência
                    </button>
                  </div>
                )}
                {open && (
                  <NutritionRecordForm
                    key={mode}
                    item={item}
                    mode={mode}
                    onCancel={closeMode}
                    onSubmit={(payload) => {
                      closeMode()
                      onRecord(item, payload)
                    }}
                  />
                )}
                {water && canRemoveWater && (
                  <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact" onClick={() => onDeleteWater(item)}>
                    <Trash2 size={14} /> Remover
                  </button>
                )}
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}

export default NutritionTimeline
